import React, { useCallback, useMemo, useRef } from "react";
import { Input } from "../components/ui/input";
import { formatMoneyGroups, parseMoney } from "../utils/moneyInput";
import { CURRENCIES } from "../utils/currency";

/**
 * Turkish-formatted money input.
 *   props.value        raw numeric (Number | null | "" ) — what is stored
 *   props.onChange(n)  called with parsed Number or null
 *   props.currency     currency code (used only to pick suffix/prefix label)
 *   props.currencyOther label for "Diğer"
 *   props.allowDecimal defaults to true
 *   props.allowNegative defaults to false
 */
export default function MoneyInput({
  value,
  onChange,
  currency = "TRY",
  currencyOther = "",
  allowDecimal = true,
  allowNegative = false,
  placeholder = "",
  className = "",
  "data-testid": testId,
  ...rest
}) {
  const inputRef = useRef(null);

  const suffix = useMemo(() => (
    currency === "Diğer" ? (currencyOther || "") :
    (CURRENCIES.find(c => c.code === currency)?.symbol || currency)
  ), [currency, currencyOther]);

  // Display: format the underlying numeric value with tr-TR grouping.
  const displayValue = useMemo(() => (
    value === null || value === undefined || value === ""
      ? ""
      : formatMoneyGroups(value)
  ), [value]);

  const handleChange = useCallback((e) => {
    const raw = e.target.value;
    // While typing keep a permissive set: digits, comma, dot, space
    const cleaned = raw.replace(/[^\d,.\- ]/g, "");
    // Track caret in raw input for stable placement
    const el = inputRef.current;
    const before = el ? el.value : "";
    const caret = el ? el.selectionStart : cleaned.length;

    if (cleaned === "" || cleaned === "-") {
      onChange(null);
      return;
    }
    const parsed = parseMoney(cleaned);
    if (parsed === null) {
      // Reject silently: keep previous value
      return;
    }
    if (!allowNegative && parsed < 0) {
      onChange(0);
      return;
    }
    if (!allowDecimal && !Number.isInteger(parsed)) {
      onChange(Math.trunc(parsed));
      return;
    }
    onChange(parsed);

    // Caret stability: on next paint, place caret at the equivalent
    // position based on digit-index (ignore separators).
    if (el) {
      requestAnimationFrame(() => {
        try {
          const newValue = formatMoneyGroups(parsed);
          // Count digits BEFORE the caret in the OLD display.
          const digitsBefore = (before.slice(0, caret).match(/\d/g) || []).length;
          // Find position in new value that has the same number of digits.
          let pos = 0, count = 0;
          for (let i = 0; i < newValue.length; i++) {
            if (/\d/.test(newValue[i])) count++;
            pos = i + 1;
            if (count >= digitsBefore) break;
          }
          el.setSelectionRange(pos, pos);
        } catch { /* ignore */ }
      });
    }
  }, [onChange, allowDecimal, allowNegative]);

  const handlePaste = useCallback((e) => {
    const text = (e.clipboardData || window.clipboardData)?.getData("text");
    if (text == null) return;
    e.preventDefault();
    const parsed = parseMoney(text);
    if (parsed === null) return;
    if (!allowNegative && parsed < 0) { onChange(0); return; }
    if (!allowDecimal && !Number.isInteger(parsed)) { onChange(Math.trunc(parsed)); return; }
    onChange(parsed);
  }, [onChange, allowDecimal, allowNegative]);

  return (
    <div className={`relative ${className}`}>
      <Input
        ref={inputRef}
        type="text"
        inputMode={allowDecimal ? "decimal" : "numeric"}
        value={displayValue}
        onChange={handleChange}
        onPaste={handlePaste}
        placeholder={placeholder}
        data-testid={testId}
        className="pr-10 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
        {...rest}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#52525B] dark:text-[#A1A1AA]">
          {suffix}
        </span>
      )}
    </div>
  );
}
